import { useCoins } from '@starsoft/common/api';
import { Tabs } from '@starsoft/common/components';
import { OriginId } from '@starsoft/common/models/origin/id';
import { useMemo } from 'react';
import { CoinType, SettingLayoutComponentId } from '../../../../../models';
import { transactionsTabsOptions } from './options';
import { TransactionModalsContentLayoutTabsProps } from './props';
import styles from './styles.module.scss';
/**
 * TransactionModalsContentLayoutTabs Component
 * This component is responsible for rendering the tabs within the transaction modals content layout.
 * It displays the tabs based on the current tab and handles tab change events.
 *
 * @param {TransactionModalsContentLayoutTabsProps} props - The properties for the component.
 * @param {string} props.currentTab - The current active tab.
 * @param {function} props.onTabChange - Callback function to handle tab change events.
 * @param {boolean} props.hidden - Indicates if the tabs should be hidden.
 *
 * @returns {JSX.Element | null} The rendered component displaying the tabs or null if hidden.
 *
 * @example
 * // Usage example:
 * <TransactionModalsContentLayoutTabs
 *   currentTab="tab1"
 *   onTabChange={(newTab) => function(newTab)}
 *   hidden={false}
 * />
 */
export default function TransactionModalsContentLayoutTabs({
  currentTab,
  onTabChange,
  hidden,
  originId,
  version,
}: TransactionModalsContentLayoutTabsProps) {
  const { coins, isLoading: isLoadingCoins } = useCoins();

  const isCryptoEnabled = useMemo(
    () => coins?.some(coin => coin?.type === CoinType.Crypto),
    [coins],
  );

  const checkIsCryptoEnabled = isCryptoEnabled && !isLoadingCoins;

  if (
    hidden ||
    originId?.toString() === OriginId.Brazil?.toString() ||
    !checkIsCryptoEnabled
  ) {
    return null;
  }

  return (
    <div className={`${styles.tabs}`}>
      <Tabs
        i18nNamespace="common"
        version={SettingLayoutComponentId.TabsV1?.toString()}
        tabs={transactionsTabsOptions}
        currentTab={currentTab}
        setTab={onTabChange}
      />
    </div>
  );
}
